import React, { useState } from "react";
import { PSY_RESOURCES, SAFE_EXCHANGE, PREVENTION_ALCOOL, PREVENTION_LINKS, TOOLS } from "@/data/studentData";
import LogoTile from "@/components/LogoTile";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HeartPulse, Phone, ShieldCheck, Users, MapPin, AlertTriangle, ExternalLink, Search } from "lucide-react";

const NEARBY_SEARCHES = [
  { key: "bapu", label: "BAPU", q: "BAPU bureau aide psychologique universitaire" },
  { key: "bu", label: "Bibliothèque universitaire", q: "bibliothèque universitaire" },
  { key: "tiers-lieux", label: "Tiers-lieux étudiants", q: "tiers-lieu étudiant maison des étudiants" },
  { key: "ru", label: "Restaurant Universitaire", q: "Restaurant Universitaire CROUS" },
  { key: "psy", label: "Psychologues / Psychiatres", q: "psychologue psychiatre" },
];

export default function BienEtre() {
  const [city, setCity] = useState("");
  const [submittedCity, setSubmittedCity] = useState("");

  const submitCity = (e) => {
    e.preventDefault();
    setSubmittedCity(city.trim());
  };

  const buildMapsLink = (q) => {
    const query = encodeURIComponent(`${q} ${submittedCity}`.trim());
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };
  const buildWazeLink = (q) => {
    const query = encodeURIComponent(`${q} ${submittedCity}`.trim());
    return `https://www.waze.com/ul?q=${query}&navigate=yes`;
  };

  return (
    <div className="space-y-10" data-testid="bienetre-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Tu n'es pas seul·e</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Bien-être & sécurité</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Aide psychologique, lieux pour échanger en sécurité, prévention alcool & stupéfiants, carte locale. Toutes les ressources sont gratuites, confidentielles et accessibles à tout étudiant.
        </p>
      </header>

      {/* Emergency callout */}
      <section
        className="rounded-2xl border border-red-200 bg-red-50 p-6 sm:p-8 flex items-start gap-4"
        data-testid="emergency-banner"
      >
        <div className="w-11 h-11 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0">
          <Phone size={20} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-red-800">Urgence — appelle</p>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">
            <span className="font-mono">15</span> SAMU · <span className="font-mono">17</span> Police · <span className="font-mono">112</span> Europe · <span className="font-mono">3114</span> Prévention suicide
          </h2>
          <p className="text-slate-700 mt-2">
            Si toi ou un proche est en détresse psychologique aiguë : appelle le <strong>3114</strong> (gratuit, 24h/24, professionnels formés). Pour une urgence vitale, le <strong>15</strong> ou <strong>112</strong>.
          </p>
        </div>
      </section>

      <Tabs defaultValue="psy" className="w-full">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="psy" data-testid="tab-psy"><HeartPulse size={14} className="mr-1.5" /> Aide psychologique</TabsTrigger>
          <TabsTrigger value="safe" data-testid="tab-safe"><Users size={14} className="mr-1.5" /> Échanges en sécurité</TabsTrigger>
          <TabsTrigger value="prev" data-testid="tab-prev"><ShieldCheck size={14} className="mr-1.5" /> Prévention</TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map"><MapPin size={14} className="mr-1.5" /> Carte locale</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools">Outils pratiques</TabsTrigger>
        </TabsList>

        <TabsContent value="psy" className="pt-6 space-y-6">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <strong>Tu peux parler en toute confidentialité.</strong> Les ressources ci-dessous sont anonymes, gratuites et tenues par des professionnels ou des étudiants formés. N'attends pas que ça aille mieux tout seul.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {PSY_RESOURCES.map((p) => <LogoTile key={p.id} item={p} />)}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {PSY_RESOURCES.filter((p) => p.contact).map((p) => (
              <div key={p.id + "-c"} className="rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4" data-testid={`psy-contact-${p.id}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="font-display font-semibold text-slate-900">{p.name}</p>
                  <p className="text-emerald-700 font-mono text-sm mt-0.5">{p.contact}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="safe" className="pt-6 space-y-3">
          {SAFE_EXCHANGE.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4" data-testid={`safe-${s.id}`}>
              <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <Users size={18} />
              </div>
              <div>
                <p className="font-display font-semibold text-slate-900">{s.title}</p>
                <p className="text-slate-600 text-sm mt-1">{s.detail}</p>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="prev" className="pt-6 space-y-6">
          <div className="space-y-3">
            {PREVENTION_ALCOOL.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-5 flex gap-4" data-testid={`prev-${p.id}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <p className="font-display font-semibold text-slate-900">{p.title}</p>
                  <p className="text-slate-600 text-sm mt-1">{p.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-800">Lignes & sites de prévention</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {PREVENTION_LINKS.map((l) => <LogoTile key={l.id} item={l} />)}
          </div>
        </TabsContent>

        <TabsContent value="map" className="pt-6 space-y-6">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <MapPin size={16} className="inline mr-1.5 text-indigo-600" />
            <strong>Trouve ce qui est proche de toi.</strong> Indique ta ville, on t'ouvre Google Maps avec les bons résultats : BAPU, bibliothèques universitaires, tiers-lieux, RU CROUS, psychologues.
          </div>
          <form
            onSubmit={submitCity}
            className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
            data-testid="map-form"
          >
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ta ville ou ton arrondissement</label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Ex : Lyon, Paris 13, Toulouse..."
                className="mt-1.5"
                data-testid="map-city-input"
              />
            </div>
            <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 h-10" data-testid="map-submit-btn">
              <Search size={16} className="mr-2" /> Rechercher
            </Button>
          </form>
          {submittedCity && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {NEARBY_SEARCHES.map((s) => (
                <div key={s.key} className="logo-tile p-5" data-testid={`map-link-${s.key}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                      <MapPin size={22} />
                    </div>
                  </div>
                  <p className="font-display font-semibold text-slate-900 mt-3">
                    {s.label} à {submittedCity}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <a
                      href={buildMapsLink(s.q)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`maps-link-${s.key}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs hover:bg-slate-800"
                    >
                      <MapPin size={12} /> Google Maps
                    </a>
                    <a
                      href={buildWazeLink(s.q)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`waze-link-${s.key}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-xs hover:bg-indigo-100"
                    >
                      Waze <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!submittedCity && (
            <p className="text-slate-400 text-sm" data-testid="map-empty-state">
              Saisis ta ville pour afficher les lieux à proximité.
            </p>
          )}
        </TabsContent>

        <TabsContent value="tools" className="pt-6 space-y-4">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 text-slate-700 text-sm">
            <strong>S'orienter, organiser ses rendez-vous.</strong> Outils utiles au quotidien : trouver son chemin, partager un point de rencontre, planifier une consultation psy ou un rendez-vous de stage.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TOOLS.map((t) => <LogoTile key={t.id} item={t} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
